import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { getTooltipId, formatNumber } from '../../../../utils/utils';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { ICON_BUTTON_ICONS } from '../../../buttons/IconButton';
import { GROSS_PROFIT_TOOLTIP } from '../../../../utils/constants/dataSources';
import { Tooltip } from 'react-tooltip';
import { getComparableCompaniesGridTemplateColumns } from './comparableCompaniesGrid';

/**
 * ComparableCompanyDataRow - Displays the target company's comparable data
 *
 * This is the special row that appears above the list of comparable companies,
 * showing the target company's financial metrics for comparison.
 */
const ComparableCompanyDataRow = ({ comparableData, layout, project, profilerAccessStore }) => {
  // Generate unique tooltip IDs (stable across renders using useMemo)
  const descriptionTooltipId = useMemo(() => getTooltipId(), []);
  const grossProfitTooltipId = useMemo(() => getTooltipId(), []);
  const basicRevenueTooltipId = useMemo(() => getTooltipId(), []);
  const basicEmployeesTooltipId = useMemo(() => getTooltipId(), []);

  const hasExtendedAccess = profilerAccessStore.canViewProfilerDetailsExtendedData(project);
  // TD-2499: CAGR 3Y is visible to Lite (details access); EBIT + EBIT-margin stay Full-only.
  const hasDetailsAccess = profilerAccessStore.canViewProfilerDetails(project);
  const desktopGridStyle =
    layout === 'desktop'
      ? {
          gridTemplateColumns: getComparableCompaniesGridTemplateColumns(
            hasExtendedAccess,
            hasDetailsAccess
          )
        }
      : {};

  return (
    <div className="item comparable-data" style={desktopGridStyle}>
      {/* Company Name - Desktop */}
      <div className="col company-name desktop-only">
        <div className="label">Company name</div>
        <div className="value">{comparableData.name || PUNCTUATION.EMPTY_VALUE}</div>
      </div>

      {/* Company Name - Mobile */}
      <div className="company-name-mobile mobile-only">
        <div className="left">
          <div className="c-name">{comparableData.name || PUNCTUATION.EMPTY_VALUE}</div>
        </div>
        <div className="right"></div>
      </div>

      {/* Location */}
      <div className="col headquarters">
        <div className="label">Location</div>
        <div className="value">{comparableData.headquarters || PUNCTUATION.EMPTY_VALUE}</div>
      </div>

      {/* Description */}
      <div className="col description">
        {layout === 'desktop' && !!comparableData.description && (
          <>
            <div
              id={descriptionTooltipId}
              className="default-black-info-circle-wrap description-similar-companies-button"
            >
              <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} alt="" />
            </div>
            <Tooltip
              anchorSelect={`#${descriptionTooltipId}`}
              multiline={true}
              variant="light"
              place="bottom-center"
              opacity={1}
              content={comparableData.description}
              className={'description-similar-companies-tooltip'}
              clickable
            />
          </>
        )}
        <div className="label">Description</div>
        <div className="value">{comparableData.description || PUNCTUATION.EMPTY_VALUE}</div>
      </div>

      {/* Revenue */}
      <div className="col revenue">
        <div className="label">Sales (€M)</div>
        <div className="value">
          {comparableData.revenue ? (
            <>
              {!!comparableData.isRevenueGrossProfit && (
                <>
                  <div id={grossProfitTooltipId} className="default-black-info-circle-wrap">
                    <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} alt="" />
                  </div>
                  <Tooltip
                    anchorSelect={`#${grossProfitTooltipId}`}
                    multiline={true}
                    variant="dark"
                    place="bottom-center"
                    opacity={1}
                    content={<>{GROSS_PROFIT_TOOLTIP}</>}
                  />
                </>
              )}
              <font
                id={basicRevenueTooltipId}
                style={comparableData.isRevenueBasic ? { color: '#348cf9' } : {}}
              >
                {comparableData.revenue}
              </font>
              <span
                style={comparableData.isRevenueBasic ? { color: '#348cf9' } : {}}
                className="year"
              >
                &lsquo;
                {comparableData.revenueYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
              </span>
              {!!comparableData.isRevenueBasic && (
                <Tooltip
                  anchorSelect={`#${basicRevenueTooltipId}`}
                  multiline={true}
                  variant="dark"
                  place="bottom-center"
                  opacity={1}
                  content={<>Based on self-disclosure or estimate</>}
                />
              )}
            </>
          ) : (
            PUNCTUATION.EMPTY_VALUE
          )}
        </div>
      </div>

      {/* CAGR 3Y - Details Access (Lite + Full) */}
      {hasDetailsAccess && (
        <div className="col revenue-yoy">
          <div className="label">CAGR 3Y (%)</div>
          <div className="value">
            {comparableData.cagr3Y ? <>{comparableData.cagr3Y}</> : PUNCTUATION.EMPTY_VALUE}
          </div>
        </div>
      )}

      {/* EBIT - Extended Access Only */}
      {hasExtendedAccess && (
        <div className="col ebit">
          <div className="label">EBIT (€M)</div>
          <div className="value">
            {comparableData.ebit ? (
              <>
                {comparableData.ebit}
                <span className="year">
                  &lsquo;
                  {comparableData.ebitYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                </span>
              </>
            ) : (
              PUNCTUATION.EMPTY_VALUE
            )}
          </div>
        </div>
      )}

      {/* EBIT Margin - Extended Access Only */}
      {hasExtendedAccess && (
        <div className="col ebit-margin">
          <div className="label">EBIT Margin (%)</div>
          <div className="value">
            {comparableData.ebitPercentage ? (
              <>{comparableData.ebitPercentage}</>
            ) : (
              PUNCTUATION.EMPTY_VALUE
            )}
          </div>
        </div>
      )}

      {/* Employees */}
      <div className="col employees no-border">
        <div className="label">Employees</div>
        <div className="value">
          {!Number.isNaN(Number(comparableData.employees)) ? (
            <>
              <font
                id={basicEmployeesTooltipId}
                style={comparableData.isEmployeesBasic ? { color: '#348cf9' } : {}}
              >
                {formatNumber((+comparableData.employees).toFixed(2))}
              </font>
              <span
                className="year"
                style={comparableData.isEmployeesBasic ? { color: '#348cf9' } : {}}
              >
                &lsquo;
                {comparableData.employeesYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
              </span>
              {!!comparableData.isEmployeesBasic && (
                <Tooltip
                  anchorSelect={`#${basicEmployeesTooltipId}`}
                  multiline={true}
                  variant="dark"
                  place="bottom-center"
                  opacity={1}
                  content={<>Based on self-disclosure or estimate</>}
                />
              )}
            </>
          ) : (
            PUNCTUATION.EMPTY_VALUE
          )}
        </div>
      </div>

      {/* Actions column (empty for comparable data row) */}
      <div className="col actions"></div>
    </div>
  );
};

ComparableCompanyDataRow.propTypes = {
  comparableData: PropTypes.object,
  layout: PropTypes.string,
  project: PropTypes.object,
  profilerAccessStore: PropTypes.object
};

export default ComparableCompanyDataRow;
